/**
 * Centralized logging utility for Dyslexa app
 * Provides structured logging with levels, timestamps, session tracking,
 * and the ability to export logs for debugging.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: Record<string, unknown>;
  /** Set when consecutive identical errors were collapsed into this one. */
  repeatCount?: number;
}

interface DeviceInfo {
  browser: string;
  os: string;
  screenSize: string;
  userAgent: string;
  online: boolean;
}

// Configuration
const MAX_LOG_ENTRIES = 300;
/*
 * Both inlined at build time from next.config.mjs, which reads the version from
 * package.json. Previously this was a hand-copied literal, and it drifted: it and
 * package.json both read 0.17.15 across several shipped batches, so a crash report
 * could not tell you which code produced it. The commit is what actually answers
 * that; the version is for humans.
 *
 * The fallbacks are what a test run or a non-Next context sees, and they are
 * deliberately not a plausible version number - a report claiming to be 'dev'
 * is obviously not from a deploy.
 */
const APP_VERSION = process.env.NEXT_PUBLIC_APP_VERSION ?? 'dev';
const BUILD_COMMIT = process.env.NEXT_PUBLIC_BUILD_COMMIT ?? 'unknown';
const PERSISTED_ERROR_LOGS_KEY = 'dyslexa_persisted_error_logs';
const MAX_PERSISTED_ERROR_LOGS = 80;

// In-memory log buffer
const logBuffer: LogEntry[] = [];

/*
 * This store holds errors only, survives reloads within the tab, and is replayed
 * into every log export for the rest of that tab's life — so its 80 slots are the
 * scarcest space in the whole reporting path.
 *
 * One repeating condition used to eat them. A production report showed 46 of the
 * 80 consumed by a single offline document, which does not just add noise: it
 * evicts the older, more varied errors that a crash report is read for. A longer
 * document would have evicted all of them.
 *
 * Consecutive errors with the same level and message therefore collapse into one
 * entry carrying a count. Matching on the message rather than the whole context is
 * the point: the 46 lines differed only by page index, which is exactly the part
 * worth losing, while the count keeps the fact that it happened 46 times.
 */
function persistErrorLog(entry: LogEntry): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const current = sessionStorage.getItem(PERSISTED_ERROR_LOGS_KEY);
    const parsed = current ? JSON.parse(current) as LogEntry[] : [];
    const sanitized: LogEntry = {
      ...entry,
      context: sanitizeContextForStorage(entry.context),
    };

    const previous = parsed.length > 0 ? parsed[parsed.length - 1] : undefined;
    let next: LogEntry[];

    if (previous && previous.level === sanitized.level && previous.message === sanitized.message) {
      // Keep the first occurrence's context, but move the timestamp to the most
      // recent one so the export shows when the run of errors ended.
      next = [
        ...parsed.slice(0, -1),
        {
          ...previous,
          timestamp: sanitized.timestamp,
          repeatCount: (previous.repeatCount ?? 1) + 1,
        },
      ];
    } else {
      next = [...parsed, sanitized].slice(-MAX_PERSISTED_ERROR_LOGS);
    }

    sessionStorage.setItem(PERSISTED_ERROR_LOGS_KEY, JSON.stringify(next));
  } catch (error) {
    console.warn('Failed to persist error log', error);
  }
}

function sanitizeContextForStorage(context?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!context) {
    return undefined;
  }

  const safeContext: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(context)) {
    if (value instanceof Error) {
      safeContext[key] = {
        name: value.name,
        message: value.message,
        stack: value.stack,
      };
      continue;
    }

    if (typeof value === 'undefined') {
      continue;
    }

    if (typeof value === 'function') {
      safeContext[key] = '[Function]';
      continue;
    }

    try {
      safeContext[key] = JSON.parse(JSON.stringify(value));
    } catch {
      safeContext[key] = String(value);
    }
  }

  return safeContext;
}

/**
 * Merges runs of consecutive entries sharing a level and message into one entry
 * carrying a count.
 *
 * The write path collapses incrementally as errors arrive, which does nothing for
 * runs already in storage. A crash report from a tab predating that fix came back
 * with 46 of its 47 log lines spent on one repeating condition — one line of
 * usable history, in a report whose whole purpose is history. So the read path
 * collapses too, and writes the result back, which is what actually frees the
 * slots for errors worth keeping.
 */
function collapseErrorRuns(entries: LogEntry[]): LogEntry[] {
  const collapsed: LogEntry[] = [];

  for (const entry of entries) {
    const previous = collapsed[collapsed.length - 1];

    if (previous && previous.level === entry.level && previous.message === entry.message) {
      collapsed[collapsed.length - 1] = {
        ...previous,
        // First occurrence's context, last occurrence's timestamp: the entry still
        // says where the run began and when it stopped.
        timestamp: entry.timestamp,
        repeatCount: (previous.repeatCount ?? 1) + (entry.repeatCount ?? 1),
      };
      continue;
    }

    collapsed.push(entry);
  }

  return collapsed;
}

function loadPersistedErrorLogs(): LogEntry[] {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const stored = sessionStorage.getItem(PERSISTED_ERROR_LOGS_KEY);
    if (!stored) {
      return [];
    }
    const parsed = JSON.parse(stored) as LogEntry[];
    if (!Array.isArray(parsed)) {
      return [];
    }

    const collapsed = collapseErrorRuns(parsed).slice(-MAX_PERSISTED_ERROR_LOGS);

    // Written back only when it shrank, so a tab carrying a run from before this
    // existed gets cleaned once rather than on every load.
    if (collapsed.length < parsed.length) {
      try {
        sessionStorage.setItem(PERSISTED_ERROR_LOGS_KEY, JSON.stringify(collapsed));
      } catch {
        // Reading worked, which is what the caller needs; a failed rewrite just
        // means the cleanup runs again next load.
      }
    }

    return collapsed;
  } catch (error) {
    console.warn('Failed to load persisted error logs', error);
    return [];
  }
}

function clearPersistedErrorLogs(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    sessionStorage.removeItem(PERSISTED_ERROR_LOGS_KEY);
  } catch (error) {
    console.warn('Failed to clear persisted error logs', error);
  }
}

const persistedErrorLogs = loadPersistedErrorLogs();
if (persistedErrorLogs.length > 0) {
  logBuffer.push(...persistedErrorLogs);
}

// Session ID - generated once per app load.
// `crypto.randomUUID` needs Safari 15.4+, so on iOS 15.0-15.3 `crypto` exists
// but the method does not. This runs at module scope, so an unguarded call
// throws before the app can render anything at all.
const sessionId =
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID().slice(0, 8)
    : Math.random().toString(36).slice(2, 10);

/**
 * Get device/browser information for log headers
 */
function getDeviceInfo(): DeviceInfo {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return {
      browser: 'server',
      os: 'server',
      screenSize: 'N/A',
      userAgent: 'server',
      online: true,
    };
  }

  const ua = navigator.userAgent;
  
  // Detect browser
  let browser = 'Unknown';
  if (ua.includes('Safari') && !ua.includes('Chrome')) {
    const match = ua.match(/Version\/(\d+\.\d+)/);
    browser = `Safari ${match ? match[1] : ''}`;
  } else if (ua.includes('Chrome')) {
    const match = ua.match(/Chrome\/(\d+)/);
    browser = `Chrome ${match ? match[1] : ''}`;
  } else if (ua.includes('Firefox')) {
    const match = ua.match(/Firefox\/(\d+)/);
    browser = `Firefox ${match ? match[1] : ''}`;
  }

  // Detect OS
  let os = 'Unknown';
  if (ua.includes('iPhone') || ua.includes('iPad')) {
    const match = ua.match(/OS (\d+[_\d]*)/);
    os = `iOS ${match ? match[1].replace(/_/g, '.') : ''}`;
  } else if (ua.includes('Android')) {
    const match = ua.match(/Android (\d+[\.\d]*)/);
    os = `Android ${match ? match[1] : ''}`;
  } else if (ua.includes('Mac OS X')) {
    os = 'macOS';
  } else if (ua.includes('Windows')) {
    os = 'Windows';
  } else if (ua.includes('Linux')) {
    os = 'Linux';
  }

  return {
    browser,
    os,
    screenSize: `${window.screen.width}x${window.screen.height}`,
    userAgent: ua,
    online: navigator.onLine,
  };
}

/**
 * Format timestamp for display (HH:MM:SS)
 */
function formatTime(date: Date): string {
  return date.toTimeString().slice(0, 8);
}

/**
 * Format full timestamp for log header
 */
function formatFullTimestamp(date: Date): string {
  return date.toISOString().replace('T', ' ').slice(0, 19);
}

/**
 * Context keys whose values can identify somebody's document.
 *
 * Crash reports upload without asking, so those uploads redact these. The
 * manual bug-report form still sends the full log, because there the user
 * chose to share it.
 */
const DOCUMENT_NAME_KEYS = new Set([
  'fileName',
  'fileNames',
  'filename',
  'sourceFileName',
]);

export interface ExportLogsOptions {
  /** Replaces document names with a placeholder. Used for automatic uploads. */
  redactDocumentNames?: boolean;
}

/**
 * Format context object for log display
 */
function formatContext(context?: Record<string, unknown>, options: ExportLogsOptions = {}): string {
  if (!context || Object.keys(context).length === 0) {
    return '';
  }
  
  const parts = Object.entries(context).map(([key, value]) => {
    // Redacted structurally rather than by pattern-matching the rendered line:
    // document names routinely contain spaces, so a regex over the formatted
    // text would strip half a name and leave the rest.
    if (options.redactDocumentNames && DOCUMENT_NAME_KEYS.has(key)) {
      return `${key}=[redacted]`;
    }

    if (value instanceof Error) {
      return `${key}=${value.message}`;
    }
    if (typeof value === 'number') {
      // Format file sizes nicely
      if (key.toLowerCase().includes('size') && value > 1024) {
        return `${key}=${(value / (1024 * 1024)).toFixed(2)}MB`;
      }
      // Format durations
      if (key.toLowerCase().includes('duration') || key.toLowerCase().includes('ms')) {
        return `${key}=${value}ms`;
      }
      return `${key}=${value}`;
    }
    return `${key}=${value}`;
  });
  
  return ' | ' + parts.join(' ');
}

/**
 * Add a log entry to the buffer
 */
function addLog(level: LogLevel, message: string, context?: Record<string, unknown>): void {
  const entry: LogEntry = {
    timestamp: new Date().toISOString(),
    level,
    message,
    context,
  };
  
  logBuffer.push(entry);
  
  // Trim buffer if too large
  if (logBuffer.length > MAX_LOG_ENTRIES) {
    logBuffer.shift();
  }

  if (level === 'error') {
    persistErrorLog(entry);
  }
  
  // Also output to console for development
  const formattedContext = formatContext(context);
  const logMessage = `[${formatTime(new Date(entry.timestamp))}] ${level.toUpperCase().padEnd(5)} ${message}${formattedContext}`;
  
  switch (level) {
    case 'error':
      console.error(logMessage);
      break;
    case 'warn':
      console.warn(logMessage);
      break;
    case 'debug':
      console.debug(logMessage);
      break;
    default:
      console.log(logMessage);
  }
}

/**
 * Export all logs as a formatted string for sharing
 */
function exportLogs(options: ExportLogsOptions = {}): string {
  const deviceInfo = getDeviceInfo();
  const now = new Date();
  
  const header = `=== Dyslexa Debug Log ===
Session: ${sessionId}
Device: ${deviceInfo.browser} / ${deviceInfo.os}
Screen: ${deviceInfo.screenSize}
Online: ${deviceInfo.online ? 'Yes' : 'No'}
Time: ${formatFullTimestamp(now)}
App Version: ${APP_VERSION} (build ${BUILD_COMMIT})
Log Count: ${logBuffer.length}

`;

  const logs = logBuffer.map(entry => {
    const time = formatTime(new Date(entry.timestamp));
    const level = entry.level.toUpperCase().padEnd(5);
    const context = formatContext(entry.context, options);
    const repeat = entry.repeatCount && entry.repeatCount > 1 ? ` (x${entry.repeatCount})` : '';
    let line = `[${time}] ${level} ${entry.message}${repeat}${context}`;
    // Append error stack when present so bug reports include full stack traces
    if (entry.context && typeof entry.context === 'object') {
      for (const value of Object.values(entry.context)) {
        if (value instanceof Error && value.stack) {
          line += '\n' + value.stack.split('\n').map(l => '  ' + l).join('\n');
          break;
        }
      }
    }
    return line;
  }).join('\n');

  return header + logs;
}

/**
 * Copy logs to clipboard and return success status
 */
async function copyLogsToClipboard(): Promise<boolean> {
  try {
    const logsText = exportLogs();
    
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      await navigator.clipboard.writeText(logsText);
      return true;
    }
    
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = logsText;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    return true;
  } catch (error) {
    console.error('Failed to copy logs to clipboard:', error);
    return false;
  }
}

/**
 * Start a timer for measuring operation duration
 * Returns a function to call when the operation completes
 */
function startTimer(label: string): (context?: Record<string, unknown>) => void {
  const startTime = Date.now();
  addLog('debug', `${label} started`);
  
  return (context?: Record<string, unknown>) => {
    const duration = Date.now() - startTime;
    addLog('info', `${label} completed`, { ...context, duration });
  };
}

/**
 * Clear all logs from buffer
 */
function clearLogs(): void {
  logBuffer.length = 0;
  clearPersistedErrorLogs();
}

/**
 * Get current session ID
 */
function getSessionId(): string {
  return sessionId;
}

/**
 * Get log count
 */
function getLogCount(): number {
  return logBuffer.length;
}

/**
 * Captures errors that never reach a React error boundary.
 *
 * Two things were previously invisible: exceptions thrown outside React's
 * render cycle (event handlers, timers) and rejected promises. The OCR flow
 * has real unhandled rejections, so a failed run could leave a spinning
 * button with nothing recorded anywhere.
 *
 * Resource failures (a chunk or image that 404s) arrive on the same 'error'
 * event but carry no Error object, so they are logged separately - a missing
 * chunk after a redeploy looks nothing like a thrown exception.
 */
const GLOBAL_HANDLERS_FLAG = '__dyslexaGlobalErrorHandlers';

function describeUnknown(value: unknown): string {
  if (value instanceof Error) {
    return value.message;
  }
  if (typeof value === 'string') {
    return value;
  }
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}

function installGlobalErrorHandlers(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const flagged = window as unknown as Record<string, boolean>;
  if (flagged[GLOBAL_HANDLERS_FLAG]) {
    return;
  }
  flagged[GLOBAL_HANDLERS_FLAG] = true;

  window.addEventListener('error', (event: ErrorEvent) => {
    try {
      const target = event.target as (HTMLElement & { src?: string; href?: string }) | null;
      const isResourceError = !!target && target !== (window as unknown as EventTarget) && !!target.tagName;

      if (isResourceError) {
        addLog('error', 'Resource failed to load', {
          tagName: target.tagName.toLowerCase(),
          url: target.src || target.href || 'unknown',
        });
        return;
      }

      addLog('error', 'Uncaught error', {
        message: event.message || describeUnknown(event.error),
        source: event.filename ? `${event.filename}:${event.lineno}:${event.colno}` : 'unknown',
        error: event.error instanceof Error ? event.error : undefined,
      });
    } catch {
      // Never let the reporter itself break the page.
    }
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    try {
      addLog('error', 'Unhandled promise rejection', {
        reason: describeUnknown(event.reason),
        error: event.reason instanceof Error ? event.reason : undefined,
      });
    } catch {
      // Never let the reporter itself break the page.
    }
  });
}

installGlobalErrorHandlers();

// Export the logger object
export const logger = {
  debug: (message: string, context?: Record<string, unknown>) => addLog('debug', message, context),
  info: (message: string, context?: Record<string, unknown>) => addLog('info', message, context),
  warn: (message: string, context?: Record<string, unknown>) => addLog('warn', message, context),
  error: (message: string, context?: Record<string, unknown>) => addLog('error', message, context),
  startTimer,
  exportLogs: (options?: ExportLogsOptions) => exportLogs(options),
  copyLogsToClipboard,
  clearLogs,
  getSessionId,
  getLogCount,
  getDeviceInfo,
};

export default logger;
